import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  OwcAvatar,
  OwcDivider,
  OwcHeader,
  OwcHeaderRow,
  OwcIconButton,
  OwcListItem,
  OwcLogo,
  OwcMenu,
  OwcTab,
  OwcTabs,
  OwcTypography,
} from '@one/react';
import { useAuth } from 'react-oidc-context';
import { snackbarService } from '@one/web-components';
import dliLogo from '../../assets/dliFavIcon.svg';
import styles from './Header.module.css';
import { decodeUserInfo } from '../../utils/authhelpers/authhelpers';
import { useGroupData } from '../../contexts/GroupDataContext';
import { isFullFeaturedGroup } from '../../utils/getDataGroupPath';
import { NAVIGATION } from '../../config/uiConfig';
import { useUserRoles } from '../../hooks/useUserRoles';

const HOME_PATH = '/dashboard';

/**
 * Resolve a config nav item's target path.
 * - pathRef: dynamic runtime value (currently 'lastDashboardPath')
 * - envKey:  external URL from process.env
 * - path:    static literal
 */
function resolveNavPath(item, refs) {
  if (item.pathRef) return refs[item.pathRef] || '/dashboard';
  if (item.envKey) return process.env[item.envKey];
  return item.path;
}

/**
 * Tab label for the active group. When the tab declares a singular override and
 * a countSource, the label follows the number of items the sidebar lists (one
 * FHIR Implementation Guide vs many), mirroring the dashboard insight tiles.
 */
function resolveTabLabel(tab, groupName, groupData) {
  const plural = tab.labelByGroup?.[groupName] || tab.labelDefault;
  const singular = tab.labelByGroupSingular?.[groupName];
  const itemsKey = tab.countSourceByGroup?.[groupName];
  if (!singular || !itemsKey) return plural;
  const items = groupData?.[itemsKey];
  return Array.isArray(items) && items.length === 1 ? singular : plural;
}

function Header() {
  const auth = useAuth();
  const [visible, setVisible] = useState(false);
  const [openNavigation, setOpenNavigation] = useState(false);
  const popupRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { groupData, lastDashboardPath } = useGroupData();
  const groupName = groupData?.groupName;
  const isLabGroup = isFullFeaturedGroup(groupName);
  const { allowManageRelease } = useUserRoles();

  /** Config-driven visibility: group capability first, then role. */
  const isNavItemVisible = useCallback(
    (item) =>
      (!item.requiresFullFeatured || isLabGroup) &&
      (!item.requiresManageReleaseRole || allowManageRelease),
    [isLabGroup, allowManageRelease]
  );

  const pathRefs = { lastDashboardPath: lastDashboardPath || '/dashboard' };

  const navItems = NAVIGATION.menu.filter(isNavItemVisible).map((item) => ({
    itemName: item.itemName,
    path: resolveNavPath(item, pathRefs),
    external: !!item.external,
  }));

  const handleLogout = useCallback(() => {
    window.location.href = '/logout';
  }, []);

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      snackbarService.show({
        message: 'Token copied to clipboard.',
        snackbarProps: {
          status: 'positive',
          duration: 2000,
          verticalPlacement: 'bottom',
          horizontalPlacement: 'center',
          // The host defaults to 50% viewport width; shrink it to the message.
          style: { width: 'fit-content', 'max-width': '90vw' },
        },
      });
    } catch (err) {
      console.error('Failed to copy token:', err);
    }
  }, []);

  const userInfo = useMemo(() => {
    if (!auth?.user?.id_token) return null;
    return decodeUserInfo(auth.user.id_token, auth.user.refresh_token);
  }, [auth?.user?.id_token, auth?.user?.refresh_token]);

  // Determine active tab based on current path (visual state only)
  const activeTab = useMemo(() => {
    if (location.pathname.startsWith('/dashboard/workflow')) return 'tab2';
    if (location.pathname.startsWith('/dashboard')) return 'tab1';
    return 'tab1';
  }, [location.pathname]);

  const closePopup = useCallback(() => setVisible(false), []);

  // Close avatar popup on click outside / ESC / scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!visible) return;
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closePopup();
      }
    };
    const handleEscPress = (event) => {
      if (visible && event.key === 'Escape') closePopup();
    };
    const handleScroll = () => visible && closePopup();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscPress);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscPress);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [visible, closePopup]);

  return (
    <OwcHeader className="owc-header" role="banner">
      <OwcHeaderRow separator variant="primary" className={styles.headerRow}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Logo + app title act as one home link. */}
          <div
            className={styles.homeLink}
            role="button"
            tabIndex={0}
            aria-label="Go to selected domain insights"
            onClick={() => navigate(HOME_PATH)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(HOME_PATH);
              }
            }}
          >
            <img src={dliLogo} alt="DLI Logo" className={styles.logo} />
            <OwcTypography variant="body2" className={styles.appTitle}>
              Data Landscape Integration
            </OwcTypography>
          </div>
          <OwcDivider direction="vertical" type="solid" />
          <OwcTabs value={activeTab} className={styles.custom_tabs}>
            {NAVIGATION.tabs.filter(isNavItemVisible).map((tab) => {
              if (tab.disabled) return <OwcTab key={tab.value} value={tab.value} disabled />;
              const label = resolveTabLabel(tab, groupName, groupData);
              return (
                <OwcTab
                  key={tab.value}
                  value={tab.value}
                  onClick={() => navigate(resolveNavPath(tab, pathRefs))}
                >
                  <OwcTypography variant="button">{label}</OwcTypography>
                </OwcTab>
              );
            })}
          </OwcTabs>
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          <div
            onMouseEnter={() => setOpenNavigation(true)}
            onMouseLeave={() => setOpenNavigation(false)}
            style={{ display: 'inline-block' }}
          >
            <OwcIconButton family="outlined" id="menu-anchor-1" icon="app_launcher"></OwcIconButton>
            <OwcMenu anchor="menu-anchor-1" limit="6" position="bottom" visible={openNavigation}>
              {navItems
                .filter((item) => !item.external)
                .map((item, index) => (
                  <OwcListItem
                    onClick={() => navigate(item.path)}
                    style={{ width: '300px' }}
                    key={index}
                    value="item1"
                  >
                    <OwcTypography
                      style={{ fontSize: '1rem', fontWeight: '500', padding: '0px 8px' }}
                    >
                      {item.itemName}
                    </OwcTypography>
                  </OwcListItem>
                ))}
              {navItems.some((item) => item.external) && (
                <>
                  <OwcTypography
                    variant="caption"
                    style={{
                      padding: '6px 12px',
                      color: '#666',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    External Links
                  </OwcTypography>
                  <OwcDivider type="solid" />
                  {navItems
                    .filter((item) => item.external)
                    .map((item, index) => (
                      <OwcListItem
                        onClick={() => window.open(item.path, '_blank', 'noopener,noreferrer')}
                        style={{ width: '300px' }}
                        key={`ext-${index}`}
                        value="item1"
                      >
                        <OwcTypography
                          style={{ fontSize: '1rem', fontWeight: '500', padding: '0px 8px' }}
                        >
                          {item.itemName}
                        </OwcTypography>
                      </OwcListItem>
                    ))}
                </>
              )}
            </OwcMenu>
          </div>
          <OwcIconButton
            flat
            icon="log_off"
            type="outlined"
            className={styles.logoutButton}
            onClick={handleLogout}
          />
          <OwcDivider direction="vertical" type="solid" />

          {/* Avatar & Popup */}
          <div
            style={{ position: 'relative', cursor: 'pointer' }}
            onClick={() => setVisible((v) => !v)}
          >
            <OwcAvatar
              role="img"
              aria-label={userInfo?.initials || 'NA'}
              size="medium"
              className="owc-avatar"
            >
              <div className="profile-initials">
                <span>{userInfo?.initials || 'NA'}</span>
              </div>
            </OwcAvatar>

            {visible && userInfo && (
              <div ref={popupRef} className={styles.popup}>
                <table className={styles.popupTable}>
                  <tbody>
                    <tr>
                      <td>Name:</td>
                      <td>{userInfo.fullName}</td>
                    </tr>
                    <tr>
                      <td>User ID:</td>
                      <td>{userInfo.userId}</td>
                    </tr>
                    <tr>
                      <td>Email:</td>
                      <td>{userInfo.email}</td>
                    </tr>
                    <tr>
                      <td>Token:</td>
                      <td
                        className={styles.tokenCell}
                        title="Click to copy"
                        onClick={() => handleCopy(userInfo.refreshToken)}
                      >
                        {userInfo.refreshToken.slice(0, 23)}...
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <OwcLogo className="TOP_BAR_ROCHE_LOGO owc-logo" />
        </div>
      </OwcHeaderRow>
    </OwcHeader>
  );
}

export default Header;
